import { ConnectDropTarget, useDrop } from 'react-dnd'
import { useMutation } from '@tanstack/react-query'
import { ItemType } from '../item-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { patchDocument } from '../../../../api/documents'
import { updateBinderDocumentParent } from '../../../../api/binder'

interface DocumentDroppable {
  isOver: boolean
  drop: ConnectDropTarget
}

type DroppableType = 'document' | 'binder'

interface DroppableItem {
  _id: string
}

export function useDocumentDroppable(
  folderId: string,
  type: DroppableType = 'document',
  enabled = true,
  targetParentId: string | null = folderId
): DocumentDroppable {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const { mutate: mutatePatchDocument } = useMutation({
    mutationFn: async (params: {
      id: string
      body: { parentId: string | null }
    }) => {
      if (type === 'binder') {
        return updateBinderDocumentParent(params)
      }

      return patchDocument(params)
    },
    onError: () => {
      showErrorMessage()
    },
  })

  const updateDocument = async (documentId: string): Promise<void> => {
    const id = documentId
    const updatedDocument = { parentId: targetParentId }
    id && mutatePatchDocument({ id, body: updatedDocument })
  }

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: [ItemType.FOLDER, ItemType.DOCUMENT],
      canDrop: (item: DroppableItem) => enabled && item?._id !== folderId,
      collect: (monitor) => ({
        isOver: enabled && monitor.isOver() && monitor.canDrop(),
      }),
      drop: (item: DroppableItem) => {
        if (!enabled) {
          return
        }

        if (item?._id !== folderId) {
          updateDocument(item._id)
        }
      },
    }),
    [enabled, folderId, type, targetParentId]
  )

  return { isOver, drop }
}
